import { derivePunchList } from '@src/utility/punchList';

import type { Video, VideoVersion } from '@src/types.ts/story';
import type { VideoResourcesMetadata } from '@src/types.ts/video';

/** A clip as the clip preview holds it, with its resources populated. */
export type PreviewClip = Partial<Video> & {
  versionId?: string;
  /** Story-query clips carry an older copy under this name; it's dropped. */
  resourcesMetadataJson?: VideoResourcesMetadata;
};

/**
 * PLAT-11911 — the clip preview's clip as Creator Studio's `loadVideo` would
 * have built it from Dato, with its own version as the only (latest) entry in
 * its version history. `null` when it lacks something the normal load fetches
 * — a missing transcript snapshot in particular would silently fall back to
 * the unedited original — or its version is unknown.
 */
export function seedFromPreviewClip(
  clip: PreviewClip,
  mainClip: PreviewClip,
): { video: Video & { versionId: string }; versions: VideoVersion[] } | null {
  const { id, versionId } = clip;
  if (!id || !versionId) return null;
  if (!Array.isArray(clip.videoSource?.elements)) return null;
  if (!clip.transcriptionSnapshot || !clip.extraElementData) return null;
  // Read with the version id, so it describes the same version. Without it
  // nothing below can tell a failed resource fetch from an absent resource.
  const { resourcesMetadata } = clip;
  if (!resourcesMetadata) return null;
  if (
    resourcesMetadata.karaokeData &&
    !clip.karaokeData &&
    !clip.karaokeDataByLanguage
  ) {
    return null;
  }
  if (resourcesMetadata.scenes && !clip.scenes) return null;

  const { resourcesMetadataJson: _alias, ...rest } = clip;
  const video = {
    ...rest,
    id,
    versionId,
    resourcesMetadata,
    // The repository defaults a missing `clip_json` the same way.
    clipJson: clip.clipJson ?? {},
    // A preview holds every variant in full; Dato returns references.
    associatedVideos: toVariantRefs(clip.associatedVideos),
    punchList: derivePunchList(clip.extraElementData),
    // Loading is snapshot-only; the legacy changelog is never replayed.
    transcriptionChanges: [],
    parentVideo: mainClip.id === id ? undefined : toParentVideo(mainClip),
  } as Video & { versionId: string };

  const latest = {
    ...video,
    meta: {
      is_current: true,
      created_at: new Date(
        clip.lastActionJson?.date ?? Date.now(),
      ).toISOString(),
    },
    // The version panel reads the editor's name; the full history replaces
    // this entry once it loads.
    editor: {},
  } as unknown as VideoVersion;

  return { video, versions: [latest] };
}

/** Variant references as Dato returns them; a save writes these ids. */
function toVariantRefs(
  variants: PreviewClip['associatedVideos'],
): Video['associatedVideos'] {
  return (variants ?? [])
    .filter(variant => !!variant.id)
    .map(({ id, aspectRatio, videoFilePrimary }) => ({
      id,
      aspectRatio,
      videoFilePrimary,
    })) as Video['associatedVideos'];
}

/** The parent as Dato returns it (`_allReferencingVideos`). */
function toParentVideo(mainClip: PreviewClip): Video['parentVideo'] {
  return {
    id: mainClip.id,
    title: mainClip.title,
    aspectRatio: mainClip.aspectRatio,
    associatedVideos: toVariantRefs(mainClip.associatedVideos),
    videoFilePrimary: mainClip.videoFilePrimary,
    clipJson: mainClip.clipJson,
    videoSource: mainClip.videoSource,
  } as Video['parentVideo'];
}
